/**
 * Phone number validation and formatting utilities
 */

import { COUNTRY_CODES } from '@/constants/countryCodes';

/**
 * Validates if a phone number is in a valid format
 * @param phoneNumber - The phone number to validate (with country code)
 * @returns true if valid, false otherwise
 */
export function isValidPhoneNumber(phoneNumber: string): boolean {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Basic validation: must be between 7 and 15 digits
  if (cleaned.length < 7 || cleaned.length > 15) {
    return false;
  }
  
  // Must start with a valid country code or local number
  return /^[1-9]\d{6,14}$/.test(cleaned);
}

/**
 * Validates if a phone number (without country code) is valid for a specific country
 * @param phoneNumber - The phone number without country code
 * @param countryCode - The country code (e.g., 'US', 'GB')
 * @returns true if valid, false otherwise
 */
export function isValidPhoneNumberForCountry(phoneNumber: string, countryCode: string): boolean {
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Basic length validation based on country
  switch (countryCode) {
    case 'GB':
      return cleaned.length >= 10 && cleaned.length <= 11;
    case 'US':
    case 'CA':
      return cleaned.length === 10;
    case 'AU':
      return cleaned.length >= 8 && cleaned.length <= 9;
    case 'DE':
    case 'FR':
    case 'IT':
    case 'ES':
      return cleaned.length >= 9 && cleaned.length <= 10;
    default:
      // For other countries, use general validation
      return cleaned.length >= 7 && cleaned.length <= 15;
  }
}

/**
 * Formats a phone number for display
 * @param phoneNumber - The phone number to format
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // If it's a UK number with country code (12-13 digits starting with 44), format as +44 XXXX XXX XXX
  if ((cleaned.length === 12 || cleaned.length === 13) && cleaned.startsWith('44')) {
    const ukNumber = cleaned.slice(2);
    if (ukNumber.length === 10) {
      return `+44 ${ukNumber.slice(0, 4)} ${ukNumber.slice(4, 7)} ${ukNumber.slice(7)}`;
    } else if (ukNumber.length === 11) {
      return `+44 ${ukNumber.slice(0, 5)} ${ukNumber.slice(5, 8)} ${ukNumber.slice(8)}`;
    }
  }
  
  // If it's a US number (10 digits), format as (XXX) XXX-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // If it's a US number with country code (11 digits starting with 1), format as +1 (XXX) XXX-XXXX
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  
  // For international numbers, just add + prefix
  if (cleaned.length > 10) {
    return `+${cleaned}`;
  }
  
  // For other cases, return as is
  return cleaned;
}

/**
 * Normalizes a phone number for storage/API calls
 * @param phoneNumber - The phone number to normalize
 * @returns Normalized phone number with + prefix
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check if the number already has a country code
  // Sort by dial code length (longest first) to avoid false matches
  const sortedCountries = [...COUNTRY_CODES].sort((a, b) => 
    b.dialCode.length - a.dialCode.length
  );
  
  for (const country of sortedCountries) {
    const dialCode = country.dialCode.replace('+', '');
    if (cleaned.startsWith(dialCode)) {
      return `+${cleaned}`;
    }
  }
  
  // For numbers without country codes, return as-is (let the component handle country selection)
  return cleaned;
}

/**
 * Gets a user-friendly error message for phone number validation
 * @param phoneNumber - The phone number that failed validation
 * @returns Error message
 */
export function getPhoneNumberErrorMessage(phoneNumber: string): string {
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  if (cleaned.length === 0) {
    return 'Phone number is required';
  }
  
  if (cleaned.length < 7) {
    return 'Phone number is too short';
  }
  
  if (cleaned.length > 15) {
    return 'Phone number is too long';
  }
  
  if (!/^[1-9]/.test(cleaned)) {
    return 'Phone number must start with a valid digit';
  }
  
  return 'Please enter a valid phone number';
}
